import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  followTournamentUseCase,
  getParticipantsByTournamentUseCase,
  getTournamentChampionUseCase,
  getTournamentFollowerCountUseCase,
  tournamentFollowRepository,
  tournamentRepository,
  unfollowTournamentUseCase,
} from '@/src/core/dependencies';
import { Participant, Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { PublicTournamentScreen } from '@/src/presentation/screens/PublicTournamentScreen';
import * as Linking from 'expo-linking';
import { logger } from '@/src/core/utils';
import Head from 'expo-router/head';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, View } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';

export default function PublicTournamentContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [champion, setChampion] = useState<Participant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      setChampion(null);

      const tournamentData = await tournamentRepository.getById(id);

      if (!tournamentData) {
        setError('Tournament not found');
        return;
      }

      if (!tournamentData.isPublic) {
        setError('This tournament is private');
        return;
      }

      const participantsData = await getParticipantsByTournamentUseCase.execute(id);

      setTournament(tournamentData);
      setParticipants(participantsData);

      if (tournamentData.status === 'completed') {
        const championData = await getTournamentChampionUseCase.execute(id);
        setChampion(championData);
      }

      // Load follower count
      const count = await getTournamentFollowerCountUseCase.execute(id);
      setFollowerCount(count);

      // Check if current user is following
      if (user) {
        const following = await tournamentFollowRepository.isFollowing(user.id, id);
        setIsFollowing(following);
      }
    } catch (error) {
      logger.error('PublicTournament', 'Failed to load public tournament', { error });
      setError('Failed to load tournament');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBracket = () => {
    if (id) {
      router.push(`/bracket/${id}`);
    }
  };

  const handleParticipantPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  const handleLocationPress = () => {
    if (tournament?.locationLat && tournament?.locationLng) {
      const latLng = `${tournament.locationLat},${tournament.locationLng}`;
      const label = encodeURIComponent(tournament.location || 'Tournament Location');

      const url = Platform.select({
        ios: `maps:?q=${label}&ll=${latLng}`,
        android: `geo:${latLng}?q=${label}`,
        web: `https://www.google.com/maps/search/?api=1&query=${latLng}`,
        default: `https://www.google.com/maps/search/?api=1&query=${latLng}`,
      });

      if (Platform.OS === 'web') {
        window.open(url, '_blank');
      } else {
        Linking.openURL(url!);
      }
    }
  };

  const handleFollowToggle = async () => {
    if (!user) {
      platformAlert(
        t('auth.loginRequired'),
        t('auth.loginRequiredMessage'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('auth.login'), onPress: () => router.push('/login') },
        ]
      );
      return;
    }

    if (!id) return;

    try {
      setFollowLoading(true);
      if (isFollowing) {
        await unfollowTournamentUseCase.execute(user.id, id);
        setIsFollowing(false);
        setFollowerCount((prev) => Math.max(0, prev - 1));
      } else {
        await followTournamentUseCase.execute(user.id, id);
        setIsFollowing(true);
        setFollowerCount((prev) => prev + 1);
      }
    } catch (error: any) {
      logger.error('PublicTournament', 'Failed to toggle follow', { error });
      platformAlert(t('common.error'), error.message || t('errors.failedToLoad'));
    } finally {
      setFollowLoading(false);
    }
  };

  // Create shareable URL for QR code
  const shareUrl = useMemo(() => {
    return Linking.createURL(`/public/${id}`);
  }, [id]);

  const seoDescription = useMemo(() => {
    if (!tournament) return '';
    const typeLabel: Record<string, string> = {
      single_elimination: 'Single Elimination',
      double_elimination: 'Double Elimination',
      round_robin: 'Round Robin',
      swiss: 'Swiss System',
    };
    const statusLabel =
      tournament.status === 'completed'
        ? 'Completed'
        : tournament.status === 'in_progress'
          ? 'In Progress'
          : 'Upcoming';
    const parts = [
      `${statusLabel} ${typeLabel[tournament.tournamentType] ?? tournament.tournamentType} tournament with ${participants.length} participant${participants.length !== 1 ? 's' : ''}.`,
    ];
    if (tournament.location) parts.push(`Location: ${tournament.location}.`);
    if (champion) parts.push(`Champion: ${champion.name}.`);
    return parts.join(' ');
  }, [tournament, participants, champion]);

  const seoUrl = `https://tournaly.app/public/${id}`;

  // Inject JSON-LD directly — avoids react-helmet-async interfering with external scripts (AdSense)
  // Must be declared before any conditional returns to comply with Rules of Hooks
  useEffect(() => {
    if (Platform.OS !== 'web' || !tournament) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'tournament-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: tournament.name,
      description: tournament.rules || seoDescription,
      url: seoUrl,
      eventStatus: 'https://schema.org/EventScheduled',
      ...(tournament.startDate && { startDate: tournament.startDate }),
      ...(tournament.location && { location: { '@type': 'Place', name: tournament.location } }),
      ...(tournament.maxParticipants && { maximumAttendeeCapacity: tournament.maxParticipants }),
      organizer: { '@type': 'SportsOrganization', name: 'Tournaly', url: 'https://tournaly.app' },
      ...(champion && { winner: { '@type': 'Person', name: champion.name } }),
    });
    document.getElementById('tournament-jsonld')?.remove();
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [tournament, champion, seoDescription, seoUrl]);

  if (loading) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (error || !tournament) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: colors.background }]}>
        <Text style={[styles.errorText, { color: colors.error }]}>{error || 'Tournament not found'}</Text>
        <Text style={[styles.errorSubtext, { color: colors.textSecondary }]}>
          This tournament may be private or no longer exist
        </Text>
      </View>
    );
  }

  return (
    <>
      <Head>
        <title>{`${tournament.name} — Tournaly`}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:title" content={`${tournament.name} — Tournaly`} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={tournament.name} />
        <meta name="twitter:description" content={seoDescription} />
      </Head>
      <Stack.Screen options={{ title: tournament.name }} />
      <PublicTournamentScreen
        tournament={tournament}
        participants={participants}
        champion={champion}
        shareUrl={shareUrl}
        isLoggedIn={!!user}
        isFollowing={isFollowing}
        followerCount={followerCount}
        followLoading={followLoading}
        handleViewBracket={handleViewBracket}
        handleParticipantPress={handleParticipantPress}
        handleLocationPress={handleLocationPress}
        handleFollowToggle={handleFollowToggle}
        colors={colors}
      />
    </>
  );
}

const styles = StyleSheet.create({
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  errorSubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
});
